import api from './api';

/**
 * Subtab 'Status onderhandelingen' (Mijn Belagom).
 *
 * Twee bronnen op /api/pool:
 *  - overzicht/herinner/vestiging/tender/archiveer: eigen cockpit-laag
 *    (poolOnderhandelingCockpitService.ts) — presentatie + drie extra acties.
 *  - reageer/contract*: PoolOnderhandelingService (openingsbod-/onderhandelings-
 *    engine) — akkoord/tegenvoorstel/afwijzen/intrekken + Model-OvO SMS-2FA.
 */

export interface OnderhandelingEvent {
  event_type: string;
  actor_rol: 'opdrachtgever' | 'professional' | 'systeem';
  tarief: number | null;
  toelichting: string | null;
  created_at: string;
}

export type OnderhandelingEngineStatus =
  | 'REQUEST_SENT'
  | 'COUNTER_PROPOSAL'
  | 'AGREED'
  | 'REJECTED'
  | 'WITHDRAWN'
  | 'CONTRACT_PENDING'
  | 'ACTIVE';

export type Tariefmodus = 'MARKTCONFORM' | 'GROEP' | 'INDIVIDUEEL';

export const TARIEFMODUS_LABEL: Record<Tariefmodus, string> = {
  MARKTCONFORM: 'Marktconform (professional bepaalt)',
  GROEP: 'Tarief per discipline',
  INDIVIDUEEL: 'Tarief op maat',
};

export type OnderhandelingCockpitStatus = 'UITGENODIGD' | 'TEGENVOORSTEL' | 'AKKOORD' | 'ACTIEF' | 'AFGEWEZEN' | 'GESLOTEN';

export interface OnderhandelingRij {
  id: string;
  engine_status: OnderhandelingEngineStatus;
  status: OnderhandelingCockpitStatus;
  professional_id: string;
  professional_naam: string;
  professional_city?: string | null;
  professional_photo_url?: string | null;
  professional_type: string;
  discipline_label: string;
  discipline_category: string | null;
  brevet_label?: string | null;
  bandbreedte_min?: number | null;
  bandbreedte_max?: number | null;
  location_id: string | null;
  vestiging: string | null;
  tariefmodus: Tariefmodus;
  bod_werkgever: number | null;
  tegenbod_professional: number | null;
  akkoord_tarief: number | null;
  huidig_bod: number | null;
  huidig_bod_door: 'opdrachtgever' | 'professional' | 'systeem';
  bericht: string | null;
  afgewezen_door: 'professional' | 'opdrachtgever' | null;
  afwijzing_reden: string | null;
  ovo_contract_id: string | null;
  contract_status: string | null;
  contract_sealed_at: string | null;
  connection_id: number | null;
  herinnering_verstuurd_op: string | null;
  herinnering_mogelijk: boolean;
  tender_vacancy_id: string | null;
  gearchiveerd_op: string | null;
  dekking_tekort: number;
  laatste_actie_op: string;
  laatste_actie_door: 'opdrachtgever' | 'professional' | 'systeem';
  laatste_actie_tekst: string | null;
  events: OnderhandelingEvent[];
}

export interface OnderhandelingenOverzicht {
  badges: {
    uitgenodigd: number;
    tegenvoorstellen: number;
    akkoord: number;
    actief: number;
    afgewezen: number;
    gesloten: number;
  };
  matrix: OnderhandelingRij[];
  dekking: Array<{ professional_type: string; label: string; categorie: string | null; gewenst: number; bezet: number; tekort: number }>;
}

export const fetchOnderhandelingenOverzicht = (inclusiefArchief = false) =>
  api.get<{ success: boolean; data: OnderhandelingenOverzicht }>(
    `/pool/employer/onderhandelingen-overzicht${inclusiefArchief ? '?archief=1' : ''}`
  );

export const herinnerOnderhandeling = (id: string) => api.post(`/pool/employer/onderhandelingen/${id}/herinner`);

export const archiveerOnderhandeling = (id: string) => api.post(`/pool/employer/onderhandelingen/${id}/archiveer`);

export const startTenderVoorOnderhandeling = (id: string, beschrijving?: string | null) =>
  api.post(`/pool/employer/onderhandelingen/${id}/tender`, { beschrijving: beschrijving ?? null });

export const zetVestigingVoorOnderhandeling = (id: string, locationId: string | null) =>
  api.patch(`/pool/employer/onderhandelingen/${id}/vestiging`, { location_id: locationId });

export type WerkgeverReactieActie = 'akkoord' | 'tegenvoorstel' | 'afwijzen' | 'intrekken';

export const werkgeverReageertOpOnderhandeling = (
  id: string,
  actie: WerkgeverReactieActie,
  opties: { tarief?: number | null; toelichting?: string | null } = {}
) => api.post(`/pool/employer/onderhandelingen/${id}/reageer`, { actie, ...opties });

/** Centrale pool-uitnodiging: meerdere disciplines tegelijk, één tekst, drie tariefmodi. */
export interface CentraleUitnodigingPayload {
  tariefmodus: Tariefmodus;
  ontvangers: Array<{ professional_id: string; professional_type: string; openingsbod?: number | null }>;
  groepstarieven?: Record<string, number> | null;
  bericht?: string | null;
  location_id?: string | null;
}

export interface CentraleUitnodigingResultaat {
  batch_id: string;
  tariefmodus: Tariefmodus;
  verstuurd: Array<{ id: string; professional_id: string; professional_type: string }>;
  fouten: Array<{ professional_id: string; professional_type: string; reden: string }>;
}

export const verstuurCentraleUitnodiging = (payload: CentraleUitnodigingPayload) =>
  api.post<{ success: boolean; message: string; data: CentraleUitnodigingResultaat }>('/pool/employer/uitnodigingen', payload);

export const fetchPoolOvoContract = (contractId: string) => api.get(`/pool/onderhandelingen/${contractId}/contract`);
export const vraagPoolTekencode = (contractId: string) => api.post(`/pool/onderhandelingen/${contractId}/contract/teken-code`);
export const ondertekenPoolContract = (contractId: string, code: string) =>
  api.post(`/pool/onderhandelingen/${contractId}/contract/onderteken`, { code });
